import jsPDF from "jspdf";
import "jspdf-autotable";
import Logo from "../../assets/nhclogo.png";
import moment from "moment/moment";

const PDF_STYLES = {
    colors: {
        primary: '#1e85e5',
        textDark: '#1a1a1a',
        textMuted: '#666666',
        textLight: '#888888',
        borderLight: '#e0e0e0',
        backgroundAlt: '#f8f9fa'
    },
    fontSize: {
        title: 16,
        subtitle: 13,
        sectionHeader: 12,
        body: 10,
        tableCell: 8,
        footer: 8
    }
};

export const generateCSV = (distroName, report, dateRange) => {
    const getAccountType = (notes) => {
        if (notes?.includes("corehealth")) return "CoreHealth";
        if (notes?.includes("teladoc")) return "Teladoc";
        return "Spending Account";
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD"
        }).format(amount);
    };

    let csvContent = "Date,Account Type,Amount\n";
    let grandTotal = 0;

    if (!Array.isArray(report)) {
        // consolidated view
        Object.entries(report).forEach(([date, payments]) => {
            if (!Array.isArray(payments)) return;
            const total = payments.reduce((sum, item) => sum + item.amount, 0);
            grandTotal += total;
            csvContent += `"${moment(date).format("MMMM DD, YYYY")}","Consolidated","${formatCurrency(total)}"\n`;
        });
    } else {
        // separated view
        report.forEach((item) => {
            grandTotal += item.amount;
            csvContent += `"${moment(item.createdAt).format("MMMM DD, YYYY")}","${getAccountType(item.notes)}","${formatCurrency(item.amount)}"\n`;
        });
    }

    csvContent += `"Total","","${formatCurrency(grandTotal)}"\n`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    const isConsolidated = !Array.isArray(report);
    const filename = `${distroName} ${isConsolidated ? "Consolidated " : ""}Deposit Summary ${dateRange || moment().format("MM-DD-YYYY")}.csv`;

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const generatePDF = (distroName, report, commissions, dateRange) => {
    const doc = new jsPDF();
    const docTitle = "Deposit Summary Report";

    jsPDF.autoTableSetDefaults({
        theme: "striped",
        tableWidth: "auto",
        columnWidth: "auto",
        showHead: "everyPage",
        rowPageBreak: "avoid",
        pageBreak: "auto",
        margin: { top: 40, bottom: 25 },
        headStyles: {
            fillColor: PDF_STYLES.colors.primary,
            textColor: '#ffffff',
            fontSize: PDF_STYLES.fontSize.tableCell,
            fontStyle: 'bold'
        },
        alternateRowStyles: {
            fillColor: PDF_STYLES.colors.backgroundAlt
        },
        styles: {
            overflow: "linebreak",
            cellPadding: 4,
            fontSize: PDF_STYLES.fontSize.tableCell,
            lineColor: PDF_STYLES.colors.borderLight,
            lineWidth: 0.25,
            textColor: PDF_STYLES.colors.textDark
        },
        columnStyles: { 1: { halign: "center", cellWidth: 20 } },
        didDrawPage: function () {
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();

            // header strip
            doc.setFillColor(PDF_STYLES.colors.primary);
            doc.rect(0, 0, pageWidth, 6, 'F');

            // logo
            doc.addImage(Logo, "PNG", 160, 12, 0, 12);

            // footer
            doc.setFontSize(PDF_STYLES.fontSize.footer);
            doc.setTextColor(PDF_STYLES.colors.textLight);
            doc.text(docTitle, 15, pageHeight - 10);
            doc.text(
                `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${doc.internal.getNumberOfPages()}`,
                pageWidth - 15,
                pageHeight - 10,
                { align: "right" }
            );
        },
    });

    const width = doc.internal.pageSize.getWidth();

    // title section
    doc.setFontSize(PDF_STYLES.fontSize.title);
    doc.setTextColor(PDF_STYLES.colors.textDark);
    doc.text("Deposit Summary Report", width / 2, 30, { align: "center" });

    doc.setFontSize(PDF_STYLES.fontSize.subtitle);
    doc.setTextColor(PDF_STYLES.colors.primary);
    doc.text(distroName, width / 2, 38, { align: "center" });

    const isConsolidated = !Array.isArray(report);
    const reportDate = isConsolidated
        ? moment(Object.keys(report)[0]).format("MMMM DD, YYYY")
        : moment(report[0].createdAt).format("MMMM DD, YYYY");

    doc.setFontSize(PDF_STYLES.fontSize.body);
    doc.setTextColor(PDF_STYLES.colors.textMuted);
    doc.text(dateRange ? dateRange : reportDate, width / 2, 46, { align: "center" });

    let finalY = 60;

    const getAccountType = (notes) => {
        if (notes?.includes("corehealth")) return "CoreHealth";
        if (notes?.includes("teladoc")) return "Teladoc";
        return "Spending Account";
    }

    // ------------------- SUMMARY TABLE -------------------
    if (report) {
        doc.setFontSize(PDF_STYLES.fontSize.sectionHeader);
        doc.setTextColor(PDF_STYLES.colors.primary);
        doc.text("Summary Report", 15, finalY - 5);

        const summaryRows = [];

        if (!Array.isArray(report)) {
            let grandtotal = 0;

            Object.keys(report).forEach(key => {
                const total = report[key].reduce((sum, item) => sum + item.amount, 0);
                grandtotal += total;

                summaryRows.push([
                    moment(key).format("MMMM DD, YYYY"),
                    " ",
                    new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD"
                    }).format(total)
                ]);
            })

            summaryRows.push(["Total", " ",
                new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD"
                }).format(grandtotal)])
        }
        else {
            report.forEach((item) => {
                summaryRows.push([
                    moment(item.createdAt).format("MMMM DD, YYYY"),
                    getAccountType(item.notes),
                    new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD"
                    }).format(item.amount)
                ]);
            });

            const total = report.reduce((sum, item) => sum + item.amount, 0);
            summaryRows.push([
                "Total", " ",
                new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD"
                }).format(total)
            ]);

        }
        doc.autoTable({
            head: [["Date", Array.isArray(report) ? "Account Type" : "", "Amount"]],
            body: summaryRows,
            styles: { halign: "right" },
            columnStyles: {
                0: { halign: "left" },
                1: { halign: "left" },
                2: { halign: "right" }
            },
            didParseCell: (data) => {
                if (data.section === "head") {
                    data.cell.styles.halign = data.column.index !== 2 ? "left" : "right";
                }
                if (data.row.index === summaryRows.length - 1) {
                    data.cell.styles.fontStyle = "bold";
                }
            },
            startY: finalY
        });

        finalY = doc.previousAutoTable.finalY + 10;
    }

    // ------------------- COMMISSIONS TABLE(S) -------------------
    if (commissions) {
        doc.setFontSize(PDF_STYLES.fontSize.sectionHeader);
        doc.setTextColor(PDF_STYLES.colors.primary);

        if (Array.isArray(commissions)) {
            // spending or separated account
            doc.text("Commissions Report", 15, finalY);
            finalY += 5;

            const rows = commissions.map(item => ([
                `${item.companyName} (${item.policyNo})`,
                new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                }).format(item.totalTransAmount)
            ]));

            const total = commissions.reduce((sum, item) => sum + item.totalTransAmount, 0);
            rows.push([
                "Total",
                new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                }).format(total)
            ]);

            doc.autoTable({
                head: [["Name", "Amount"]],
                body: rows,
                startY: finalY,
                styles: { halign: "right" },
                columnStyles: {
                    0: { halign: "left" },
                    1: { halign: "right" }
                },
                didParseCell: (data) => {
                    if (data.section === "head") {
                        data.cell.styles.halign = data.column.index === 0 ? "left" : "right";
                    }
                    if (data.row.index === rows.length - 1) {
                        data.cell.styles.fontStyle = "bold";
                    }
                },
            });
        }
        else if (typeof commissions === "object") {
            Object.entries(commissions).forEach(([accountType, commissionList]) => {
                if (!Array.isArray(commissionList)) return;

                doc.setFontSize(PDF_STYLES.fontSize.sectionHeader);
                doc.setTextColor(PDF_STYLES.colors.primary);
                doc.text(`Commissions - ${getAccountType(accountType)}`, 15, finalY);
                finalY += 5;

                const rows = commissionList.map(item => ([
                    `${item.companyName} (${item.policyNo})`,
                    new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD'
                    }).format(item.totalTransAmount)
                ]));

                const total = commissionList.reduce((sum, item) => sum + item.totalTransAmount, 0);
                rows.push([
                    "Total",
                    new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD'
                    }).format(total)
                ]);

                doc.autoTable({
                    head: [["Name", "Amount"]],
                    body: rows,
                    startY: finalY,
                    styles: { halign: "right" },
                    columnStyles: {
                        0: { halign: "left" },
                        1: { halign: "right" }
                    },
                    didParseCell: (data) => {
                        if (data.section === "head") {
                            data.cell.styles.halign = data.column.index === 0 ? "left" : "right";
                        }
                        if (data.row.index === rows.length - 1) {
                            data.cell.styles.fontStyle = "bold";
                        }
                    },
                });

                finalY = doc.previousAutoTable.finalY + 10;
            });
        }
    }

    const filename = `${distroName} ${isConsolidated ? "Consolidated" : ""} Deposit Summary Report ${moment(reportDate).format("MM-DD-YYYY")}.pdf`;
    doc.save(filename);
};